import React, { useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import { getItem } from '../helper/localstorage.helper';
import { formatNumberWithCommas, formatNumberSwedish } from '../helper/salary.helpers';
import SalaryResultCharts from './salary-result-charts.component';
import PillTabs from './dashboard/pill-tabs.component';

interface SalaryData {
  net?: string | number;
  amountIncludingVat?: string | number;
  vat?: string | number;
  vatPercentage?: string | number;
  amount?: string | number;
  fee?: string | number;
  socialFee?: string | number;
  feeInSEK?: string | number;
  grossInSek?: string | number;
  tax?: string | number;
  taxInSek?: string | number;
  socialFeeInSek?: string | number;
  toPay?: string | number;
  toPayInSek?: string | number;
  socialFeeRateDeduction?: number;
  taxRateDeduction?: number;
  fakturaFeePercentage?: number;
  workingHours: Array<{ amount?: number; }>;
  pension: string | number;
  pensionSocialFee: string | number;
  holidayPayAmount?: string | number;
  pensionInSek?: string | number;
  holidayPayAmountInSek?: string | number;
  currencyMarginPercentage?: string | number;
  currencyMarginAmount?: string | number;
  netInSek?: string | number;
}

interface SalaryResultCardProps {
  t: (key: string) => string;
  data: SalaryData;
  currencyName: string;
  activeTab: 'invoice' | 'gross' | 'net';
  selectedVat: string | number;
  travelVat: string | number;
  materialsToolsVat: string | number;
  customExpenses: Array<{ id: number; type: string; amount: number; vat: number }>;
  violet?: boolean;
}

const RowItem: React.FC<{ label: string; value: string }> = React.memo(({ label, value }) => (
  <div className="d-flex justify-content-between align-items-start mb-2">
    <span className="section-title flex-shrink-1 pe-2" style={{ wordBreak: 'break-word' }}>{label}</span>
    <span className="value flex-shrink-0 text-end" style={{ minWidth: 'fit-content' }}>{value}</span>
  </div>
));

RowItem.displayName = 'RowItem';

// Utility function to sum all workingHours entries by description and apply VAT
export function sumByDescriptionWithVat(
  workingHours: Array<{ amount?: number; workingDescription?: string }> = [],
  desc: string,
  vat: string | number
): number {
  if (!Array.isArray(workingHours)) return 0;
  const sum = workingHours
    .filter(
      exp => exp && exp.workingDescription === desc && typeof exp.amount === 'number'
    )
    .reduce((acc, exp) => acc + (exp.amount ?? 0), 0);
  const v = typeof vat === 'number' ? vat : Number(vat);
  if (isNaN(sum) || isNaN(v)) return 0;
  return sum + (sum * v / 100);
}


const SalaryResultCard: React.FC<SalaryResultCardProps> = ({ t, data, currencyName, activeTab, selectedVat, travelVat, materialsToolsVat, customExpenses, violet = false }) => {
  const {
    net = "00",
    amountIncludingVat = "00",
    vat = "00",
    vatPercentage = "0",
    amount = "00",
    fee = "00",
    socialFee = "00",
    feeInSEK = "00",
    grossInSek = "00",
    tax = "00",
    taxInSek = "00",
    socialFeeInSek = "00",
    toPay = "00",
    socialFeeRateDeduction = 0,
    taxRateDeduction = 0,
    fakturaFeePercentage = 0,
    pension = "00",
    pensionSocialFee = "00",
    holidayPayAmount = "00",
    pensionInSek = "00",
    holidayPayAmountInSek = "00",
    toPayInSek = "00",
    currencyMarginPercentage = "00",
    currencyMarginAmount = "00",
    netInSek = "00",
    workingHours = Array<{ amount?: number; }>(),
  } = data || {};

  const { isSwedish, format } = useMemo(() => {
    const isSwedish = getItem('i18nextLng') === 'sv-se';
    const format = (val: string | number) =>
      ` ${isSwedish ? formatNumberSwedish(val) : formatNumberWithCommas(val)}`;
    return { isSwedish, format };
  }, []);

  // Invoice amounts are in the SELECTED currency; salary/fees are converted to
  // SEK. So label invoice rows with the chosen currency (USD/EUR/GBP), but keep
  // SEK's localised "kr"/"SEK" label for SEK.
  const invoiceCurLabel = currencyName === 'SEK' ? t('kr') : currencyName;

  const { noteTitle, noteDesc, feeNoteTitle, feeNoteDesc } = useMemo(() => {
    const [title = '', desc = ''] = t('NOTE_ESTIMATE').split(':');
    const [feeTitle = '', feeDesc = ''] = t('NOTE_FEE_RANGE').split(':');
    return { noteTitle: title, noteDesc: desc, feeNoteTitle: feeTitle, feeNoteDesc: feeDesc };
  }, [t]);

  const { headingText, mainValue } = useMemo(() => {
    const headingText = (() => {
      switch (activeTab) {
        case 'invoice':
          return t('INVOICE_AMOUNT');
        case 'gross':
          return t('GROSS_SALARY_RESULT');
        default:
          return t('NET_SALARY_RESULT');
      }
    })();

    const mainValue = (() => {
      switch (activeTab) {
        case 'invoice':
          return `${amount} ${currencyName === 'SEK' ? t('kr') : currencyName}`;
        case 'net':
          return `SEK ${format(currencyName)=== "SEK" ?format(net) : format(netInSek)}`;
        default:
          return `SEK ${format(grossInSek)}`;
      }
    })();

    return { headingText, mainValue };
  }, [activeTab, t, currencyName, amount, net, grossInSek]);

  const feeMapping: Record<number, number> = {
    23.91: 31.42,
    9.26: 10.21,
    0: 0
  };


  const netValue = `${format(currencyName) === "SEK" ? format(toPay) : format(toPayInSek)} ${t('kr')}`;

  const [showCharts, setShowCharts] = useState(false);

  return (
    <Card className={`salary-card ${violet ? 'vs-result' : 'margin-left-166'}`}>
      <div className={violet ? 'vs-result__hero' : ''}>
        <div className="vs-result__hero-main">
          <h4 className="mb-2 salary-heading text-break">{headingText}</h4>
          <h3 className="Heading_h3 pb-4 text-break mb-0">{format(mainValue)}</h3>
        </div>
        {violet && (
          <PillTabs
            ariaLabel={t('SALARY_BREAKDOWN_CHARTS')}
            activeTab={showCharts ? 'charts' : 'details'}
            onTabChange={(key) => setShowCharts(key === 'charts')}
            tabs={[
              {
                key: 'details',
                label: t('DETAILS'),
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                    <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                  </svg>
                ),
              },
              {
                key: 'charts',
                label: t('CHARTS'),
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
                  </svg>
                ),
              },
            ]}
          />
        )}
      </div>

      <div className={violet ? 'vs-result__body' : ''}>
      <div className={violet ? 'vs-view' : ''} key={showCharts ? 'view-charts' : 'view-details'}>
      {violet && showCharts ? (
        <SalaryResultCharts compact t={t} data={data} currencyName={currencyName} activeTab={activeTab} />
      ) : (
      <>
      <div className="salary-line pt-4" />

      <section>
        <h6 className="salary-section-title pb-4 mb-0 pt-2">{t('INVOICE_DETAILS')}</h6>
        <RowItem
          label={t('INVOICE_AMOUNT_INC_VAT')}
          value={`${format(amountIncludingVat)} ${invoiceCurLabel}`}
        />
        <RowItem
          label={`${t('VAT_PERCENT')} (${vatPercentage}%)`}
          value={`${format(vat)} ${invoiceCurLabel}`}
        />
        <RowItem
          label={t('INVOICE_AMOUNT_EXC_VAT')}
          value={ `${format(amount)} ${invoiceCurLabel}`}
        />
      </section>

      <div className="salary-line pt-4" />

      <section>
        <h6 className="salary-section-title pb-4 mb-0">{t('FEES')}</h6>
        <RowItem
          label={`${t('INVOIZO_FEE')} (${fakturaFeePercentage}%)`}   
          value={`${format(currencyName).includes("SEK") ? format(fee)  : format(feeInSEK)} ${t('kr')}`}        />
        {currencyName !== "SEK" && (
         <RowItem
          label={`${t('INVOIZO_CURRENCY_MARGIN')} (${currencyMarginPercentage}%)`}
          value={` ${format(currencyMarginAmount)} ${t('kr')}`}
        />)}
        <RowItem
          label={`${t('SOCIAL_FEES')} (${feeMapping[socialFeeRateDeduction] ?? socialFeeRateDeduction}%)`}
          value={` ${format(currencyName).includes("SEK") ? format(socialFee) : format(socialFeeInSek)} ${t('kr')}`}
        />
      </section>

      <div className="salary-line pt-4" />


      <section>

        <h3 className="salary-section-title pb-4 mb-0">{t('SALARY_COMPONENTS')}</h3>
        <RowItem
          label={t('GROSS_SALARY_HOLIDAY')}
          value={`${format(grossInSek)} ${t('kr')}`}
        />
        <div className="salary-line pt-4" />

        <h3 className="salary-section-title pb-4 mb-0">{t('TAX_INFORMATION')}</h3>
        <RowItem
          label={`${t('INCOME_TAX')} (${taxRateDeduction}%)`}
          value={`${format(currencyName)=== "SEK" ?format(tax) : format(taxInSek)} ${t('kr')}`}
        />
        <div className="salary-line pt-4" />


        <h3 className="salary-section-title pb-4 mb-0">{t('Holiday allowance')}</h3>
        <RowItem
          label={t('Holiday Fee')}
          value={`${format(currencyName)=== "SEK" ?format(holidayPayAmount) : format(holidayPayAmountInSek)} ${t('kr')}`}
        />
        <div className="salary-line pt-4" />

        <h3 className="salary-section-title pb-4 mb-0">{t('Pension')}</h3>
        <RowItem
          label={t('Pension Social Fee')}
          value={`${format(pensionSocialFee)} ${t('kr')}`}
        />
        <RowItem
          label={t('Pension Amount')}
          value={`${format(currencyName)=== "SEK" ?format(pension) : format(pensionInSek)} ${t('kr')}`}
        />
        <div className="salary-line pt-4" />

        {activeTab.includes('invoice') && (
          <>
            {/* <h3 className="salary-section-title pb-4 mb-0">{t('ALLOWANCES')}</h3> */}
            <RowItem
              label={t('Mileage Report')}
              value={`${format(
                sumByDescriptionWithVat(workingHours, 'Mileage Report', selectedVat)
              )} ${t('kr')}`}
            />
            <RowItem
              label={t('Allowance in Sweden')}
              value={`${format(
                sumByDescriptionWithVat(workingHours, 'Allowance in Sweden', selectedVat)
              )} ${t('kr')}`}
            />
            <RowItem
              label={t('Allowance outside Sweden')}
              value={`${format(
                sumByDescriptionWithVat(workingHours, 'Allowance outside Sweden', selectedVat)
              )} ${t('kr')}`}
            />
            <RowItem
              label={t('Travel Expenses')}
              value={`${format(
                sumByDescriptionWithVat(workingHours, 'Travel Expenses as per attached PDF',
                  (typeof travelVat === 'number' ? travelVat : Number(travelVat)) +
                  customExpenses.reduce((acc, v) => acc + (typeof v.vat === 'number' ? v.vat : Number(v.vat)), 0)

                )
              )} ${t('kr')}`}
            />
            <RowItem
              label={t('Expenses for materials and tools')}
              value={`${format(
                // Sum standard material/tools
                sumByDescriptionWithVat(
                  workingHours,
                  'Expenses for material and tools as per attached pdf',
                  (typeof materialsToolsVat === 'number' ? materialsToolsVat : Number(materialsToolsVat)) +
                  customExpenses.reduce((acc, v) => acc + (typeof v.vat === 'number' ? v.vat : Number(v.vat)), 0)
                )
              )} ${t('kr')}`}
            />
          </>
        )}
      </section>

      <div className="salary-line pt-4" />

      {violet ? (
        <div className="vs-result__net">
          <div>
            <h3 className="salary-section-title mb-1">{t('NET_SALARY_RESULT')}</h3>
            <h3 className="value mb-0">{netValue}</h3>
          </div>
          <span className="vs-result__net__wallet" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
              <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
            </svg>
          </span>
        </div>
      ) : (
        <div className="d-flex justify-content-between align-items-start">
          <h3 className="salary-section-title flex-shrink-1 pe-2">{t('NET_SALARY_RESULT')}</h3>
          <h3 className="value flex-shrink-0">{netValue}</h3>
        </div>
      )}

      <div className="note w-75">
        <b className="salary-note-bold">{noteTitle}:</b> {noteDesc}
      </div>
      <div className="note w-75 mt-2">
        <b className="salary-note-bold">{feeNoteTitle}:</b> {feeNoteDesc}
      </div>
      </>
      )}
      </div>
      </div>
    </Card>
  );
};

export default SalaryResultCard;
